import { Col, Row } from "antd"
import cx from "clsx"
import {
  Button,
  DataTable,
  DateFormat,
  Group,
  Icon,
  notification,
  Space,
  Tag,
  Text,
  AiFeedbackForm,
  Divider,
  Modal,
  CollapsibleText,
  Avatar,
  FLagWithText,
  DetailBar,
  Flex,
} from "components/common"
import Recording from "components/common/recording/recording"
import { IconTextDate, Separator, TitleDescription } from "components/custom"
import { useGetCallDetailQuery, useGetTaskLogsQuery } from "features/clients/detail/api"
import { ProjectModal, StaffInfo } from "features/shared/components"
import { useLazyGetEditedTaskDetailsQuery, useGetTaskTypesQuery } from "features/tasks/api"
import { t } from "i18next"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useSelector } from "react-redux"
import {
  capitalizeFirstLetter,
  convertToReadableString,
  isWithinLast24Hours,
  joinBytItems,
  localisedValue,
} from "utils"
import { fireEvent } from "utils/gtm/gtmEventHandler"
import { ACTION_TYPES } from "utils/gtm/gtmEventsData"
import getPrivilege from "utils/privileges/privileges"
import theme from "utils/themeVars"
import { AddTaskModal, TaskFilters } from ".."
import styles from "./index.module.less"
import { SvgIconAi } from "components/svg"
import EditedTaskTooltip from "features/shared/components/editedTaskTooltip/editedTaskTooltip"
import useManageExpandedRowIds from "hooks/useExpandedRowIds"
import CallDrawer from "features/callRecording/components/callDrawer/callDrawer"
import EditIconForEditTask from "features/shared/components/editIconForEditTask/editIconForEditTask"
import UserProfileSummary from "features/shared/components/userProfileSummary"
import MeetingTaskStatus from "../taskStatus/meetingTaskStatus"
import { checkMeetingTaskType } from "utils"
const getColumns = () => [
  { title: t("Task"), dataIndex: "taskStatus", key: "taskStatus", width: 150, className: "tableStartSpace" },
  {
    title: t("Remarks"),
    dataIndex: "remarks",
    key: "remarks",
    width: 210,
  },
  { title: t("Staff"), dataIndex: "staff", key: "staff", width: 100 },
  { title: t("Lead Id"), dataIndex: "leadId", key: "leadId", width: 100 },
]

const TasksTable = props => {
  const {
    client,
    fontSize,
    stickyOffset,
    lead,
    filterClass,
    from = {},
    getCount = () => {},
    tsrTable,
    leadPrivileges,
    tableStyle,
    tsrToggle,
    clientLeads,
    isEditable,
    onEditClick,
    showVerificationIcons,
  } = props
  const defaultFilters = { sortBy: "id", sortOrder: "desc", pageLimit: 10, page: 0, clientTaskAllocatedUsers: true }
  const [filterObject, setFilterObject] = useState({
    ...defaultFilters,
    ...lead,
  })
  const [rowIds, updateRowIds, doesRowIdExist] = useManageExpandedRowIds()
  const [selectedLead, setSelectedLead] = useState()
  const { data, isFetching, error, refetch } = useGetTaskLogsQuery({
    clientId: client?.id,
    filterObject,
  })
  const [taskId, setTaskId] = useState()
  const [selectedTask, setSelectedTask] = useState(null)
  const [transcription, setTranscription] = useState()
  const modalRef = useRef()

  const [
    getEditedTaskDetails,
    { data: editedTaskData, isFetching: editedTaskDataFetching, error: editedTaskError, refetch: editedTaskRefetch },
  ] = useLazyGetEditedTaskDetailsQuery()
  const { data: taskTypes } = useGetTaskTypesQuery()
  const projectRef = useRef()
  const addTaskRef = useRef()
  const aiFeedbackRef = useRef()
  const callDrawerRef = useRef()

  const userPrivileges = useSelector(state => state.app.privileges)

  useEffect(() => {
    data?.count && getCount(data?.count)
  }, [data?.count])

  useEffect(() => {
    setFilterObject({ ...defaultFilters, ...lead })
  }, [lead])

  const handleDrawer = task => {
    setSelectedTask(task)
    callDrawerRef?.current?.toggleDrawer()
  }

  const onChangePage = (page, pageSize) => {
    setFilterObject(prev => ({ ...prev, pageLimit: pageSize, page: page }))
  }

  const handleSearch = (objFilter, filterLabels) => {
    setFilterObject({ ...objFilter })
    fireEvent({ ...ACTION_TYPES.when_filter_is_applied, ...from, ...filterLabels })
  }

  const onSort = obj => {
    setFilterObject(prev => ({ ...prev, sortBy: obj.columnName, sortOrder: obj.sortOrder }))
  }

  const getStatusColors = status => {
    if (status === "pending" || status === "undecided") {
      return { color: "#D9D9D9" }
    }
    if (status === "approved") {
      return { color: "#2CB270", textColor: "#FFFFFF" }
    }
    if (status === "rejected") {
      return { color: "#F39797", textColor: "#FFFFFF" }
    }
  }

  const modifiedLead =
    lead?.id && lead?.Project
      ? lead
      : {
          id: lead?.id || selectedLead?.leadId,
          Project: lead?.Project || { name: localisedValue({ key: "projectName", object: selectedLead?.interest }) },
        }

  const taskTableData = useCallback(
    (data, showEditButton) => {
      const tableDataItem = ({ item }) => {
        const canEdit =
          showEditButton && isEditable && item?.canEdit && getPrivilege("can_edit_task_on_lead", userPrivileges)
        return {
          key: item?.id,

          leadInfo: (
            <>
              <div>{item?.leadId}</div>
              <TitleDescription
                title={
                  getPrivilege(["can_view_client_project_summary"], leadPrivileges) ? (
                    <a rel="noreferrer"> {localisedValue({ key: "projectName", object: item?.interest })}</a>
                  ) : (
                    localisedValue({ key: "projectName", object: item?.interest })
                  )
                }
                onClick={e1 => {
                  e1.stopPropagation()
                  if (getPrivilege(["can_view_client_project_summary"], leadPrivileges)) {
                    projectRef.current.showFor(item?.InternalInquiry?.Project?.projectId)
                  }
                }}
                gapSpace={4}
                description={
                  item?.interest &&
                  joinBytItems(
                    localisedValue({ object: item?.interest, key: "projectDesc" })?.filter(x => x.trim()),
                    <Icon icon="GoDotFill" size=".7em" color="#D6D6D6" />
                  )
                }
              />
            </>
          ),

          leadId: (
            <IconTextDate title={item?.leadId} date={item?.createdAt} customTooltip={t(`Lead Added on`) + ": "} />
          ),

          taskStatus: (
            <TitleDescription
              color={item?.taskStatus.name === "SalesClosed(Won)" ? "" : undefined}
              title={
                <Space align="center" size={4}>
                  {localisedValue({ key: "name", object: item?.taskStatus })
                    ?.filter(x => x.trim())
                    .join("-") || ""}{" "}
                  {item?.verified && !showVerificationIcons && (
                    <Icon icon={"MdOutlineCheckCircle"} size="18px" style={{ color: theme["primary-color"] }} />
                  )}
                  {item?.QualityCheckTask && (
                    <Tag {...getStatusColors(item?.QualityCheckTask?.status)}>{` ${t("QC")}: ${convertToReadableString(
                      localisedValue({
                        object: item?.QualityCheckTask,
                        key: "status",
                      })
                    )}`}</Tag>
                  )}
                  {showVerificationIcons && checkMeetingTaskType(item?.TaskType?.key) && (
                    <MeetingTaskStatus data={item} />
                  )}
                </Space>
              }
              description={<DateFormat date={item?.taskStatus?.date} />}
            />
          ),

          remarks: (
            <Group gap="2px" key={item?.id}>
              {item?.remark?.desc && item?.aiDescription ? (
                <>
                  <Flex align="start" gap="8px" wrap="wrap">
                    <Avatar
                      src={item?.AssignedTo?.logo}
                      size={25}
                      style={{ fontSize: "12px" }}
                      colorKey={item?.AssignedTo?.name?.[0].toLowerCase()}
                    >
                      {item?.AssignedTo?.name?.[0]}
                    </Avatar>
                    <Flex flex="1" style={{ minWidth: 0 }}>
                      <CollapsibleText
                        text={localisedValue({ key: "desc", object: item?.remark })}
                        collapsibleLength={50}
                      />
                    </Flex>
                  </Flex>
                  <Divider dashed className="m-0  mbe-8" style={{ borderColor: "#c3c3c3" }} />
                  <div style={{ wordBreak: "normal" }}>
                    <SvgIconAi size={25} style={{ verticalAlign: -3 }} />
                    <CollapsibleText
                      text={localisedValue({ key: "aiDescription", object: item })}
                      collapsibleLength={50}
                    />
                  </div>
                </>
              ) : item?.remark?.desc ? (
                <Flex align="start" gap="8px" wrap="wrap">
                  <Avatar
                    src={item?.AssignedTo?.logo}
                    size={25}
                    style={{ fontSize: "12px" }}
                    colorKey={item?.AssignedTo?.name?.[0].toLowerCase()}
                  >
                    {item?.AssignedTo?.name?.[0]}
                  </Avatar>
                  <Flex flex="1" style={{ minWidth: 0 }}>
                    <CollapsibleText
                      text={localisedValue({ key: "desc", object: item?.remark })}
                      collapsibleLength={50}
                    />
                  </Flex>
                </Flex>
              ) : (
                item?.aiDescription && (
                  <div style={{ wordBreak: "normal" }}>
                    <SvgIconAi size={25} style={{ verticalAlign: -3 }} />
                    <CollapsibleText
                      text={localisedValue({ key: "aiDescription", object: item })}
                      collapsibleLength={50}
                    />
                  </div>
                )
              )}
              {item?.remark?.attachment?.length ? (
                <Row align="middle" style={{ gap: "2px 8px" }} className="fs12">
                  {item?.remark?.attachment.map((attachment, index) => (
                    <a key={index} href={attachment?.path} target="_blank" rel="noreferrer">
                      <Icon style={{ color: theme["primary-color"] }} icon="BsFileEarmarkText" size="1em" />{" "}
                      {t("Attachment")}
                      {index + 1}
                    </a>
                  ))}
                </Row>
              ) : null}
              {!!item?.commentVoiceRecording && (
                <Col>
                  <div style={{ width: 210 }}>
                    <Recording
                      className="w-100"
                      id={item?.commentVoiceRecording?.id}
                      recordingSrc={item?.commentVoiceRecording?.path}
                    />
                  </div>
                  {item?.audioNoteTranscription && (
                    <Button
                      text={t("View Transcript")}
                      type="link"
                      style={{
                        marginTop: "-10px",
                        "--btn-color": theme["text-color-secondary"],
                      }}
                      className="pointer text-underline p-0 mbe-8"
                      onClick={() => {
                        setTranscription(item?.audioNoteTranscription)
                        modalRef?.current?.setVisible(true)
                      }}
                    />
                  )}
                </Col>
              )}
            </Group>
          ),
          staff: (
            <Flex align="center" gap="6px">
              <Avatar
                src={item?.AssignedTo?.logo}
                size={25}
                style={{ fontSize: "12px" }}
                colorKey={item?.AssignedTo?.name?.[0].toLowerCase()}
              >
                {item?.AssignedTo?.name?.[0]}
              </Avatar>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div>
                  {localisedValue({ object: item?.AssignedTo, key: "name" })}&nbsp;
                  <UserProfileSummary staffId={item?.AssignedTo?.id} />
                </div>
                <div className="gray-800 fs14">
                  {joinBytItems(
                    [
                      localisedValue({ object: item?.AssignedTo?.staff?.staffDelegation, key: "short" }),
                      localisedValue({ object: item?.AssignedTo?.staff?.staffCity, key: "name" }),
                    ],
                    <Separator />
                  )}
                </div>
              </div>
            </Flex>
          ),
        }
      }
      return data?.map((item, index) => tableDataItem({ item: item }))
    },
    [data, editedTaskData, rowIds]
  )

  return (
    <>
      {(client?.leadIdsWithTasks || clientLeads) && (
        <TaskFilters
          client={client}
          filterObject={filterObject}
          leadIds={client?.leadIdsWithTasks || clientLeads}
          onSearch={handleSearch}
          onSort={onSort}
          filterClass={filterClass}
          from={from}
          tsrToggle={tsrTable}
          staffList={data?.taskAllocatedUsers}
          taskTypes={taskTypes}
        />
      )}

      <DataTable
        tableLayout="auto"
        pagination={{ totalCount: data?.count, pageOptions: [10, 25, 50] }}
        paginationOnBottom={true}
        paginationOnTop={false}
        onChangePage={onChangePage}
        columns={getColumns()}
        data={taskTableData(data?.data, true)}
        error={error}
        loading={isFetching}
        onErrorRetry={refetch}
        tableStyle={{ ...tableStyle, fontSize }}
        noUrlPush={true}
        stickyOffset={stickyOffset}
        pageSize={filterObject.pageLimit}
        expandedRowKeys={rowIds}
        expandable={{
          expandIcon: () => null,
          showExpandColumn: false,
          expandRowByClick: true,
          expandedRowClassName: () => "expandedTableOuter",
          expandedRowRender: (_record, index) => (
            <DataTable
              mainClass={styles.expandedTable}
              tableLayout="auto"
              columns={getColumns()}
              data={taskTableData(editedTaskData)}
              error={editedTaskError}
              loading={editedTaskDataFetching}
              onErrorRetry={editedTaskRefetch}
              tableStyle={{ "--padding-left": "20px" }}
              skeletonRowCount={1}
            />
          ),
        }}
        rowClassName={(_record, index) =>
          !!doesRowIdExist(data?.data[index]?.id) ? cx(styles.rowView, styles.expandedParentRow) : styles.rowView
        }
      />
      <ProjectModal ref={projectRef} />
      <AddTaskModal lead={modifiedLead} client={client} formRef={addTaskRef} isEditable={isEditable} />
      <AiFeedbackForm ref={aiFeedbackRef} entityId={taskId} entity={"task_log"} />
      <CallDrawer
        useQuery={useGetCallDetailQuery}
        selectedCall={selectedTask}
        onBack={toggleCallDrawer => {
          toggleCallDrawer()
        }}
        ref={callDrawerRef}
        canAddFeedback={getPrivilege("can_add_feedback", userPrivileges)}
      />
      <Modal
        title={t("Meeting Review Transcription")}
        ref={modalRef}
        width={1000}
        onCancel={() => {
          setTranscription(null)
          modalRef?.current?.setVisible(false)
        }}
        footer={null}
        destroyOnClose
        bodyStyle={{ paddingTop: 15 }}
      >
        {transcription}
      </Modal>
    </>
  )
}
export default TasksTable

TasksTable.defaultProps = {
  fontSize: 14,
}
